import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../atoms/button/button';
import { Sheet, SheetContent } from '../../../atoms/sheet/sheet';
import { FilterType, ReadOnlyFilter } from '../filter-utils';

import {
  DateFilterComponent,
  MultiSelectFilterComponent,
  SelectFilterComponent,
  TextFilterComponent,
} from './filter-components';

type FiltersProps = {
  readonly filters: FilterType[];
  readonly findTempFilter: (id: string) => ReadOnlyFilter | undefined;
  readonly updateDateFilter: IFilterSidebarProps['updateDateFilter'];
  readonly updateTextFilter: IFilterSidebarProps['updateTextFilter'];
  readonly updateSelectFilter: IFilterSidebarProps['updateSelectFilter'];
  readonly updateMultiSelectFilter: IFilterSidebarProps['updateMultiSelectFilter'];
};

function renderDateFilter(
  filter: Extract<FilterType, { kind: 'date' }>,
  temp: Extract<ReadOnlyFilter, { kind: 'date' }>,
  onDateChange: FiltersProps['updateDateFilter']
) {
  return (
    <DateFilterComponent
      fromDate={temp.value.from ?? undefined}
      id={filter.id}
      key={filter.id}
      name={filter.name}
      onDateChange={onDateChange}
      toDate={temp.value.to ?? undefined}
    />
  );
}

function renderTextFilter(
  filter: Extract<FilterType, { kind: 'text' }>,
  temp: Extract<ReadOnlyFilter, { kind: 'text' }>,
  onTextChange: FiltersProps['updateTextFilter']
) {
  return (
    <TextFilterComponent
      id={filter.id}
      key={filter.id}
      name={filter.name}
      onTextChange={onTextChange}
      value={temp.value}
    />
  );
}

function renderSelectFilter(
  filter: Extract<FilterType, { kind: 'select' }>,
  temp: Extract<ReadOnlyFilter, { kind: 'select' }>,
  onValueChange: FiltersProps['updateSelectFilter']
) {
  return (
    <SelectFilterComponent
      defaultValue={filter.defaultValue}
      id={filter.id}
      key={filter.id}
      name={filter.name}
      onValueChange={onValueChange}
      selectedValue={temp.value}
      values={filter.values}
    />
  );
}

function renderMultiSelectFilter(
  filter: Extract<FilterType, { kind: 'multi-select' }>,
  temp: Extract<ReadOnlyFilter, { kind: 'multi-select' }>,
  onValueChange: FiltersProps['updateMultiSelectFilter']
) {
  return (
    <MultiSelectFilterComponent
      defaultValue={filter.defaultValue}
      id={filter.id}
      key={filter.id}
      name={filter.name}
      onValueChange={onValueChange}
      selectedValues={temp.value}
      values={filter.values}
    />
  );
}

function Filters({
  filters,
  findTempFilter,
  updateDateFilter,
  updateTextFilter,
  updateSelectFilter,
  updateMultiSelectFilter,
}: {
  readonly filters: FilterType[];
  readonly findTempFilter: (id: string) => ReadOnlyFilter | undefined;
  readonly updateDateFilter: IFilterSidebarProps['updateDateFilter'];
  readonly updateTextFilter: IFilterSidebarProps['updateTextFilter'];
  readonly updateSelectFilter: IFilterSidebarProps['updateSelectFilter'];
  readonly updateMultiSelectFilter: IFilterSidebarProps['updateMultiSelectFilter'];
}) {
  return (
    <>
      {filters.map((filter) => {
        const tempFilter = findTempFilter(filter.id);
        if (!tempFilter) return null;
        if (filter.kind === 'date' && tempFilter.kind === 'date') {
          return renderDateFilter(filter, tempFilter, updateDateFilter);
        }
        if (filter.kind === 'text' && tempFilter.kind === 'text') {
          return renderTextFilter(filter, tempFilter, updateTextFilter);
        }

        if (filter.kind === 'select' && tempFilter.kind === 'select') {
          return renderSelectFilter(filter, tempFilter, updateSelectFilter);
        }

        if (filter.kind === 'multi-select' && tempFilter.kind === 'multi-select') {
          return renderMultiSelectFilter(filter, tempFilter, updateMultiSelectFilter);
        }

        return null;
      })}
    </>
  );
}

function SaveFilterButton({ onSave }: { readonly onSave: () => void }) {
  const { t } = useTranslation();
  return (
    <div>
      <Button appearance="primary" className="w-full" onClick={onSave} size="sm">
        {t('ui-components.table.filters.saveFilters')}
      </Button>
    </div>
  );
}
interface IFilterSidebarProps {
  filters: FilterType[];
  isInitialized: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  tempFilters: ReadOnlyFilter[];
  updateDateFilter: (id: string, key: 'from' | 'to', value: Date | null) => void;
  updateTextFilter: (id: string, value: string | null) => void;
  updateSelectFilter: (id: string, value: string | null) => void;
  updateMultiSelectFilter: (id: string, values: string[] | null) => void;
}

function useFindTempFilter(tempFilters: ReadOnlyFilter[]) {
  return React.useCallback(
    (id: string): ReadOnlyFilter | undefined => {
      return tempFilters.find((f) => f.id === id);
    },
    [tempFilters]
  );
}

const FilterSidebar: React.FC<IFilterSidebarProps> = ({
  filters,
  isInitialized,
  isOpen,
  onClose,
  onSave,
  tempFilters,
  updateDateFilter,
  updateTextFilter,
  updateSelectFilter,
  updateMultiSelectFilter,
}) => {
  const findTempFilter = useFindTempFilter(tempFilters);

  if (!isInitialized) return null;
  return (
    <Sheet onOpenChange={onClose} open={isOpen}>
      <SheetContent title="All Filters">
        <div className="flex flex-col gap-6">
          <Filters
            filters={filters}
            findTempFilter={findTempFilter}
            updateDateFilter={updateDateFilter}
            updateMultiSelectFilter={updateMultiSelectFilter}
            updateSelectFilter={updateSelectFilter}
            updateTextFilter={updateTextFilter}
          ></Filters>
          <SaveFilterButton onSave={onSave}></SaveFilterButton>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export { Filters, FilterSidebar };
